import React, { useState, CSSProperties } from 'react';

interface ComplexNumber {
  r: number;
  i: number;
}

type QuantumState = (number | ComplexNumber)[];

interface BlochVector {
  x: number;
  y: number;
  z: number;
  theta: number;
  phi: number;
}

interface Probabilities {
  p0: number;
  p1: number;
}

interface Probabilities2Q {
  [key: string]: number;
}

const App: React.FC = () => {
  const [numQubits, setNumQubits] = useState<number>(1);
  const [state, setState] = useState<QuantumState>([1, 0]);
  const [state2q, setState2q] = useState<QuantumState>([1, 0, 0, 0]);
  const [animating, setAnimating] = useState<boolean>(false);

  const GATES_1Q: { [key: string]: (number | ComplexNumber)[][] } = {
    X: [[0, 1], [1, 0]],
    Y: [[0, { r: 0, i: -1 }], [{ r: 0, i: 1 }, 0]],
    Z: [[1, 0], [0, -1]],
    H: [[1/Math.sqrt(2), 1/Math.sqrt(2)], [1/Math.sqrt(2), -1/Math.sqrt(2)]],
    S: [[1, 0], [0, { r: 0, i: 1 }]],
  };

  const GATES_2Q: { [key: string]: number[][] } = {
    CNOT: [
      [1, 0, 0, 0],
      [0, 1, 0, 0],
      [0, 0, 0, 1],
      [0, 0, 1, 0]
    ],
    SWAP: [
      [1, 0, 0, 0],
      [0, 0, 1, 0],
      [0, 1, 0, 0],
      [0, 0, 0, 1]
    ]
  };

  const mulComplex = (a: number | ComplexNumber, b: number | ComplexNumber): ComplexNumber => {
    if (typeof a === 'number') a = { r: a, i: 0 };
    if (typeof b === 'number') b = { r: b, i: 0 };
    return {
      r: a.r * b.r - a.i * b.i,
      i: a.r * b.i + a.i * b.r
    };
  };

  const addComplex = (a: number | ComplexNumber, b: number | ComplexNumber): ComplexNumber => {
    if (typeof a === 'number') a = { r: a, i: 0 };
    if (typeof b === 'number') b = { r: b, i: 0 };
    return { r: a.r + b.r, i: a.i + b.i };
  };

  const normalize = (vec: QuantumState): QuantumState => {
    let norm: number = 0;
    for (let v of vec) {
      const c: ComplexNumber = typeof v === 'number' ? { r: v, i: 0 } : v;
      norm += c.r * c.r + c.i * c.i;
    }
    norm = Math.sqrt(norm);
    return vec.map(v => {
      if (typeof v === 'number') return v / norm;
      const complex = v as ComplexNumber;
      return { r: complex.r / norm, i: complex.i / norm };
    });
  };

  const matrixVectorMul = (matrix: (number | ComplexNumber)[][], vector: QuantumState): QuantumState => {
    const result: QuantumState = [];
    for (let i = 0; i < matrix.length; i++) {
      let sum: ComplexNumber = { r: 0, i: 0 };
      for (let j = 0; j < vector.length; j++) {
        sum = addComplex(sum, mulComplex(matrix[i][j], vector[j]));
      }
      result.push(sum);
    }
    return result;
  };

  const applyGate1Q = (gateName: string): void => {
    setAnimating(true);
    const gate = GATES_1Q[gateName];
    const [a, b] = state;

    const r1 = addComplex(mulComplex(gate[0][0], a), mulComplex(gate[0][1], b));
    const r2 = addComplex(mulComplex(gate[1][0], a), mulComplex(gate[1][1], b));

    setState(normalize([r1, r2]));
    setTimeout(() => setAnimating(false), 300);
  };

  const applyGate2Q = (gateName: string): void => {
    setAnimating(true);
    const gate = GATES_2Q[gateName];
    setState2q(normalize(matrixVectorMul(gate, state2q)));
    setTimeout(() => setAnimating(false), 300);
  };

  const applyGate2Q_toQubit = (gateName: string, qubitIndex: number): void => {
    setAnimating(true);
    const gate = GATES_1Q[gateName];

    let matrix: (number | ComplexNumber)[][];
    if (qubitIndex === 0) {
      matrix = [
        [gate[0][0], gate[0][1], 0, 0],
        [gate[1][0], gate[1][1], 0, 0],
        [0, 0, gate[0][0], gate[0][1]],
        [0, 0, gate[1][0], gate[1][1]]
      ];
    } else {
      matrix = [
        [gate[0][0], 0, gate[0][1], 0],
        [0, gate[0][0], 0, gate[0][1]],
        [gate[1][0], 0, gate[1][1], 0],
        [0, gate[1][0], 0, gate[1][1]]
      ];
    }

    setState2q(normalize(matrixVectorMul(matrix, state2q)));
    setTimeout(() => setAnimating(false), 300);
  };

  const getBlochVector = (): BlochVector => {
    let [a, b] = state;
    if (typeof a === 'number') a = { r: a, i: 0 };
    if (typeof b === 'number') b = { r: b, i: 0 };

    const aabs = Math.sqrt((a as ComplexNumber).r * (a as ComplexNumber).r + (a as ComplexNumber).i * (a as ComplexNumber).i);
    const theta = 2 * Math.acos(Math.max(-1, Math.min(1, aabs)));
    const phi = Math.atan2((b as ComplexNumber).i, (b as ComplexNumber).r);

    return {
      x: Math.sin(theta) * Math.cos(phi),
      y: Math.sin(theta) * Math.sin(phi),
      z: Math.cos(theta),
      theta,
      phi
    };
  };

  const getProbabilities = (): Probabilities => {
    const [a, b] = state;
    const pa = typeof a === 'number' ? a * a : (a as ComplexNumber).r * (a as ComplexNumber).r + (a as ComplexNumber).i * (a as ComplexNumber).i;
    const pb = typeof b === 'number' ? b * b : (b as ComplexNumber).r * (b as ComplexNumber).r + (b as ComplexNumber).i * (b as ComplexNumber).i;
    return { p0: pa, p1: pb };
  };

  const getProbabilities2Q = (): Probabilities2Q => {
    const probs: Probabilities2Q = {};
    const bases = ['|00⟩', '|01⟩', '|10⟩', '|11⟩'];
    for (let i = 0; i < 4; i++) {
      const a = state2q[i];
      probs[bases[i]] = typeof a === 'number' ? a * a : (a as ComplexNumber).r * (a as ComplexNumber).r + (a as ComplexNumber).i * (a as ComplexNumber).i;
    }
    return probs;
  };

  const complexToString = (c: number | ComplexNumber): string => {
    if (typeof c === 'number') return c.toFixed(3);
    const complex = c as ComplexNumber;
    const sign = complex.i >= 0 ? '+' : '';
    return `${complex.r.toFixed(2)}${sign}${complex.i.toFixed(2)}i`;
  };

  const { x, y } = getBlochVector();
  const { p0, p1 } = getProbabilities();

  const scale = 80;
  const cx = 100, cy = 100;
  const vx = cx + x * scale;
  const vy = cy - y * scale;

  const containerStyle: CSSProperties = {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #0f172a 100%)',
    color: '#fff',
    padding: '24px',
    fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif'
  };

  const maxWidthStyle: CSSProperties = {
    maxWidth: '1280px',
    margin: '0 auto'
  };

  const titleStyle: CSSProperties = {
    fontSize: '42px',
    fontWeight: 'bold',
    background: 'linear-gradient(90deg, #60a5fa, #22d3ee, #a855f7)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    marginBottom: '8px'
  };

  const subtitleStyle: CSSProperties = {
    fontSize: '14px',
    color: '#9ca3af',
    marginBottom: '32px'
  };

  const buttonGroupStyle: CSSProperties = {
    display: 'flex',
    gap: '16px',
    marginBottom: '32px'
  };

  const buttonStyle: CSSProperties = {
    padding: '10px 24px',
    borderRadius: '8px',
    fontWeight: '600',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.2s',
    fontSize: '14px'
  };

  const gridStyle: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr',
    gap: '32px'
  };

  const cardStyle: CSSProperties = {
    background: 'rgba(17, 24, 39, 0.5)',
    backdropFilter: 'blur(10px)',
    borderRadius: '16px',
    padding: '32px',
    border: '1px solid rgba(55, 65, 81, 0.3)',
    transition: 'all 0.3s'
  };

  const cardTitleStyle: CSSProperties = {
    fontSize: '20px',
    fontWeight: '600',
    marginBottom: '24px',
    color: '#60a5fa'
  };

  const blochContainerStyle: CSSProperties = {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '32px'
  };

  const svgStyle: CSSProperties = {
    background: 'rgba(31, 41, 55, 0.4)',
    borderRadius: '12px',
    border: '1px solid rgba(55, 65, 81, 0.3)',
  };

  const paramGridStyle: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '16px',
    marginTop: '16px'
  };

  const paramCardStyle: CSSProperties = {
    background: 'rgba(31, 41, 55, 0.4)',
    padding: '16px',
    borderRadius: '8px',
    border: '1px solid rgba(55, 65, 81, 0.3)'
  };

  const paramLabelStyle: CSSProperties = {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '8px'
  };

  const paramValueStyle: CSSProperties = {
    fontSize: '24px',
    fontWeight: 'bold',
    fontFamily: 'monospace',
    color: '#06b6d4'
  };

  const gateGridStyle: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '12px',
    marginBottom: '24px'
  };

  const gateButtonStyle: CSSProperties = {
    padding: '12px',
    borderRadius: '8px',
    border: 'none',
    fontFamily: 'monospace',
    fontWeight: 'bold',
    fontSize: '12px',
    cursor: 'pointer',
    background: 'linear-gradient(135deg, #2563eb, #1d4ed8)',
    color: '#fff',
    transition: 'all 0.2s',
  };

  const resetButtonStyle: CSSProperties = {
    width: '100%',
    padding: '12px',
    borderRadius: '8px',
    border: 'none',
    fontWeight: '600',
    cursor: 'pointer',
    background: 'linear-gradient(90deg, #374151, #1f2937)',
    color: '#fff',
    transition: 'all 0.2s'
  };

  const probContainerStyle: CSSProperties = {
    marginBottom: '16px'
  };

  const probLabelStyle: CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '8px',
    fontSize: '14px'
  };

  const probBarStyle: CSSProperties = {
    background: '#1f2937',
    borderRadius: '9999px',
    height: '12px',
    overflow: 'hidden'
  };

  const probFillStyle = (width: number): CSSProperties => ({
    background: 'linear-gradient(90deg, #06b6d4, #22d3ee)',
    height: '100%',
    borderRadius: '9999px',
    transition: 'width 0.3s',
    width: `${width}%`
  });

  const amplitudeBoxStyle: CSSProperties = {
    background: 'rgba(31, 41, 55, 0.4)',
    padding: '12px',
    borderRadius: '8px',
    border: '1px solid rgba(55, 65, 81, 0.3)',
    fontFamily: 'monospace',
    fontSize: '12px',
    color: '#10b981',
    marginBottom: '8px'
  };

  const stateBoxStyle: CSSProperties = {
    textAlign: 'center',
    fontFamily: 'monospace',
    fontSize: '12px',
    background: 'rgba(31, 41, 55, 0.4)',
    padding: '16px',
    borderRadius: '8px',
    border: '1px solid rgba(55, 65, 81, 0.3)',
    color: '#06b6d4',
    fontWeight: 'bold'
  };

  return (
    <div style={containerStyle}>
      <div style={maxWidthStyle}>
        <div style={{ marginBottom: '32px' }}>
          <div style={titleStyle}>⚛️ QuantumVis</div>
          <div style={subtitleStyle}>Interactive quantum state visualization</div>
        </div>

        <div style={buttonGroupStyle}>
          {[1, 2].map(n => (
            <button
              key={n}
              onClick={() => setNumQubits(n)}
              style={{
                ...buttonStyle,
                ...(numQubits === n
                  ? { background: '#2563eb', color: '#fff', boxShadow: '0 0 20px rgba(37, 99, 235, 0.3)' }
                  : { background: '#1f2937', color: '#d1d5db' }
                )
              }}
            >
              {n} qubit{n === 1 ? '' : 's'}
            </button>
          ))}
        </div>

        {numQubits === 1 ? (
          <div style={{ display: 'grid', gridTemplateColumns: '1 / -1', gap: '32px' }}>
            <div style={gridStyle}>
              <div>
                <div style={cardStyle}>
                  <div style={cardTitleStyle}>Bloch Sphere</div>
                  <div style={blochContainerStyle}>
                    <svg width="240" height="240" viewBox="0 0 200 200" style={svgStyle}>
                      <circle cx={cx} cy={cy} r={scale} fill="none" stroke="#475569" strokeWidth="1.5" opacity="0.4" />
                      <circle cx={cx} cy={cy} r={scale * 0.7} fill="none" stroke="#334155" strokeWidth="1" opacity="0.3" />
                      <line x1={cx - scale} y1={cy} x2={cx + scale} y2={cy} stroke="#64748b" strokeWidth="1.5" opacity="0.6" />
                      <line x1={cx} y1={cy - scale} x2={cx} y2={cy + scale} stroke="#64748b" strokeWidth="1.5" opacity="0.6" />
                      <ellipse cx={cx} cy={cy} rx={scale} ry={scale * 0.35} fill="none" stroke="#1e293b" strokeWidth="1.5" opacity="0.5" />
                      <line x1={cx} y1={cy} x2={vx} y2={vy} stroke="#0ea5e9" strokeWidth="3" strokeLinecap="round" />
                      <circle cx={vx} cy={vy} r="6" fill="#0ea5e9" />
                      <text x={cx + scale + 8} y={cy + 5} fontSize="12" fill="#cbd5e1">X</text>
                      <text x={cx - 12} y={cy - scale - 10} fontSize="12" fill="#cbd5e1">Z</text>
                    </svg>
                  </div>
                  <div style={paramGridStyle}>
                    <div style={paramCardStyle}>
                      <div style={paramLabelStyle}>θ</div>
                      <div style={paramValueStyle}>{(getBlochVector().theta * 180 / Math.PI).toFixed(1)}°</div>
                    </div>
                    <div style={paramCardStyle}>
                      <div style={paramLabelStyle}>φ</div>
                      <div style={paramValueStyle}>{(getBlochVector().phi * 180 / Math.PI).toFixed(1)}°</div>
                    </div>
                    <div style={paramCardStyle}>
                      <div style={paramLabelStyle}>Purity</div>
                      <div style={paramValueStyle}>{(p0 * p0 + p1 * p1).toFixed(2)}</div>
                    </div>
                  </div>
                </div>

                <div style={cardStyle}>
                  <div style={cardTitleStyle}>Single-Qubit Gates</div>
                  <div style={gateGridStyle}>
                    {Object.keys(GATES_1Q).map(gate => (
                      <button
                        key={gate}
                        onClick={() => applyGate1Q(gate)}
                        disabled={animating}
                        style={{
                          ...gateButtonStyle,
                          opacity: animating ? 0.5 : 1,
                          cursor: animating ? 'not-allowed' : 'pointer'
                        }}
                      >
                        {gate}
                      </button>
                    ))}
                  </div>
                  <button onClick={() => setState([1, 0])} style={resetButtonStyle}>
                    ↺ Reset
                  </button>
                </div>
              </div>

              <div>
                <div style={cardStyle}>
                  <div style={cardTitleStyle}>Probabilities</div>
                  <div style={probContainerStyle}>
                    <div style={probLabelStyle}>
                      <span>|0⟩</span>
                      <span style={{ color: '#06b6d4', fontWeight: 'bold' }}>{(p0 * 100).toFixed(1)}%</span>
                    </div>
                    <div style={probBarStyle}>
                      <div style={probFillStyle(p0 * 100)} />
                    </div>
                  </div>
                  <div style={probContainerStyle}>
                    <div style={probLabelStyle}>
                      <span>|1⟩</span>
                      <span style={{ color: '#ec4899', fontWeight: 'bold' }}>{(p1 * 100).toFixed(1)}%</span>
                    </div>
                    <div style={probBarStyle}>
                      <div style={{ ...probFillStyle(p1 * 100), background: 'linear-gradient(90deg, #ec4899, #f472b6)' }} />
                    </div>
                  </div>
                </div>

                <div style={cardStyle}>
                  <div style={cardTitleStyle}>Amplitudes</div>
                  <div style={paramLabelStyle}>α</div>
                  <div style={amplitudeBoxStyle}>{complexToString(state[0])}</div>
                  <div style={paramLabelStyle}>β</div>
                  <div style={amplitudeBoxStyle}>{complexToString(state[1])}</div>
                </div>

                <div style={cardStyle}>
                  <div style={cardTitleStyle}>State |ψ⟩</div>
                  <div style={stateBoxStyle}>
                    {complexToString(state[0])}|0⟩ + {complexToString(state[1])}|1⟩
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div style={gridStyle}>
            <div>
              <div style={cardStyle}>
                <div style={cardTitleStyle}>Two Qubits |ψ⟩</div>

                <div style={{ background: 'rgba(31, 41, 55, 0.4)', padding: '16px', borderRadius: '8px', marginBottom: '16px', border: '1px solid rgba(55, 65, 81, 0.3)' }}>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#a78bfa', marginBottom: '12px' }}>Two-Qubit Gates</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                    {Object.keys(GATES_2Q).map(gate => (
                      <button
                        key={gate}
                        onClick={() => applyGate2Q(gate)}
                        disabled={animating}
                        style={{
                          ...gateButtonStyle,
                          background: 'linear-gradient(135deg, #7c3aed, #6d28d9)',
                          opacity: animating ? 0.5 : 1,
                          cursor: animating ? 'not-allowed' : 'pointer'
                        }}
                      >
                        {gate}
                      </button>
                    ))}
                  </div>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '12px', color: '#06b6d4', marginBottom: '8px', fontWeight: '600' }}>Qubit 0 Gates</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                    {['X', 'H', 'Z'].map(gate => (
                      <button
                        key={`q0-${gate}`}
                        onClick={() => applyGate2Q_toQubit(gate, 0)}
                        disabled={animating}
                        style={{
                          ...gateButtonStyle,
                          background: 'rgba(37, 99, 235, 0.5)',
                          opacity: animating ? 0.5 : 1,
                          cursor: animating ? 'not-allowed' : 'pointer',
                          fontSize: '11px'
                        }}
                      >
                        {gate}₀
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#ec4899', marginBottom: '8px', fontWeight: '600' }}>Qubit 1 Gates</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                    {['X', 'H', 'Z'].map(gate => (
                      <button
                        key={`q1-${gate}`}
                        onClick={() => applyGate2Q_toQubit(gate, 1)}
                        disabled={animating}
                        style={{
                          ...gateButtonStyle,
                          background: 'rgba(236, 72, 153, 0.5)',
                          opacity: animating ? 0.5 : 1,
                          cursor: animating ? 'not-allowed' : 'pointer',
                          fontSize: '11px'
                        }}
                      >
                        {gate}₁
                      </button>
                    ))}
                  </div>
                </div>

                <button onClick={() => setState2q([1, 0, 0, 0])} style={{ ...resetButtonStyle, marginTop: '16px' }}>
                  ↺ Reset
                </button>
              </div>
            </div>

            <div>
              <div style={cardStyle}>
                <div style={cardTitleStyle}>Probabilities</div>
                {Object.entries(getProbabilities2Q()).map(([basis, prob]) => (
                  <div key={basis} style={probContainerStyle}>
                    <div style={probLabelStyle}>
                      <span>{basis}</span>
                      <span style={{ color: '#a78bfa', fontWeight: 'bold' }}>{(prob * 100).toFixed(1)}%</span>
                    </div>
                    <div style={probBarStyle}>
                      <div style={{ ...probFillStyle(prob * 100), background: 'linear-gradient(90deg, #a78bfa, #c4b5fd)' }} />
                    </div>
                  </div>
                ))}
              </div>

              <div style={cardStyle}>
                <div style={cardTitleStyle}>Amplitudes</div>
                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {state2q.map((amp, idx) => (
                    <div key={idx} style={amplitudeBoxStyle}>
                      α_{idx}: {complexToString(amp)}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default App;
